import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import BackToTop from '@/components/base/BackToTop';
import Footer from '@/pages/home/components/Footer';
import SolarHero from './components/SolarHero';
import SolarStats from './components/SolarStats';
import SolarBenefits from './components/SolarBenefits';
import SolarSolutions from './components/SolarSolutions';
import SolarProcess from './components/SolarProcess';
import SolarRebates from './components/SolarRebates';
import SolarFAQ from './components/SolarFAQ';
import SolarCTA from './components/SolarCTA';

const PAGE_TITLE = 'Solar Energy Solutions Australia | Trusted Solar Providers — AK Energy';
const PAGE_DESCRIPTION = 'Discover trusted solar energy providers across Australia. Compare residential solar, commercial solar, and battery storage solutions. Find CEC-accredited installers and government rebates on AK Energy.';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Solar Energy Solutions Australia',
  description: PAGE_DESCRIPTION,
  url: 'https://ak-energyau.com/solar-energy-australia',
  inLanguage: 'en-AU',
  isPartOf: {
    '@type': 'WebSite',
    name: 'AK Energy Australia',
    url: 'https://ak-energyau.com',
  },
  about: {
    '@type': 'Thing',
    name: 'Solar Energy',
    description: 'Renewable energy generated from sunlight using photovoltaic panels or solar thermal systems.',
  },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://ak-energyau.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Solar Energy Australia',
        item: 'https://ak-energyau.com/solar-energy-australia',
      },
    ],
  },
};

export default function SolarEnergyPage() {
  useEffect(() => {
    // Set page title
    document.title = PAGE_TITLE;

    // Set meta description
    let metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (metaDesc) {
      metaDesc.content = PAGE_DESCRIPTION;
    }

    // Set canonical
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) {
      canonical.href = 'https://ak-energyau.com/solar-energy-australia';
    }

    // OG tags
    const ogTitle = document.querySelector<HTMLMetaElement>('meta[property="og:title"]');
    if (ogTitle) ogTitle.content = 'Solar Energy Solutions Australia | AK Energy';
    const ogDesc = document.querySelector<HTMLMetaElement>('meta[property="og:description"]');
    if (ogDesc) ogDesc.content = PAGE_DESCRIPTION;
    const ogUrl = document.querySelector<HTMLMetaElement>('meta[property="og:url"]');
    if (ogUrl) ogUrl.content = 'https://ak-energyau.com/solar-energy-australia';

    // Twitter tags
    const twTitle = document.querySelector<HTMLMetaElement>('meta[name="twitter:title"]');
    if (twTitle) twTitle.content = 'Solar Energy Solutions Australia | AK Energy';
    const twDesc = document.querySelector<HTMLMetaElement>('meta[name="twitter:description"]');
    if (twDesc) twDesc.content = PAGE_DESCRIPTION;

    // Keywords
    let metaKw = document.querySelector<HTMLMetaElement>('meta[name="keywords"]');
    if (metaKw) {
      metaKw.content = 'solar energy Australia, solar panels Australia, solar installers, solar rebates Australia, commercial solar, battery storage Australia, residential solar';
    }

    // Restore on unmount
    return () => {
      document.title = 'AK Energy Australia | Renewable Energy Solutions Marketplace';
    };
  }, []);

  return (
    <main className="relative bg-background-50">
      {/* Structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <Navbar />
      <SolarHero />
      <SolarStats />
      <SolarBenefits />
      <SolarSolutions />
      <SolarProcess />
      <SolarRebates />
      <SolarFAQ />
      <SolarCTA />
      <Footer />
      <BackToTop />
    </main>
  );
}